import { AxiosInstance } from "axios";
import { mockCases } from "../utils/mockData";

// Configuration
const ENABLE_MOCK_DATA = process.env.REACT_APP_ENABLE_MOCK_DATA === "true";
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

// Interfaces for the new API
export interface CaseFilters {
  page?: number;
  limit?: number;
  status?: 'pending' | 'processing' | 'ready' | 'failed' | 'all';
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ReviewData {
  specialistDiagnosis: string;

  recommendations: string;
  notes?: string;
}

export interface CasesResponse {
  cases: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default class ReviewerApi {
  constructor(private api: AxiosInstance) {}

  getProfile() {
    if (IS_DEVELOPMENT && ENABLE_MOCK_DATA) {
      return Promise.resolve({
        data: {
          id: "3",
          name: "Rita Reviewer",
          email: "reviewer@example.com",
          role: "specialist",
          specialization: "Dermatopathology",
          experience: "8 years"
        }
      });
    }
    return this.api.get("/specialist/profile");
  }

  // New API method for fetching cases with filters
  async getCasesForReview(filters: CaseFilters = {}): Promise<CasesResponse> {
    if (IS_DEVELOPMENT && ENABLE_MOCK_DATA) {
      const casesToReview = mockCases.filter(c => !c.reviewed);
      return Promise.resolve({ 
        cases: casesToReview,
        pagination: {
          page: filters.page || 1,
          limit: filters.limit || 10,
          total: casesToReview.length,
          totalPages: Math.ceil(casesToReview.length / (filters.limit || 10))
        }
      });
    }

    // Build query parameters
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await this.api.get(`/specialist/cases?${params.toString()}`);
    return response.data;
  }

  // New API method for reviewing a case
  async reviewCase(caseId: string, reviewData: ReviewData): Promise<any> {
    if (IS_DEVELOPMENT && ENABLE_MOCK_DATA) {
      const index = mockCases.findIndex(c => c.id === caseId);
      if (index !== -1) {
        mockCases[index] = {
          ...mockCases[index],
          reviewed: true,
          reviewedDiagnosis: reviewData.specialistDiagnosis,
          reviewerNote: reviewData.notes || '',
          status: "Completed"
        };
        return Promise.resolve({
          data: { 
            message: "Case reviewed successfully.",
            case: mockCases[index] 
          }
        });
      }
      return Promise.reject({ response: { data: { message: "Case not found" } } });
    }

    const response = await this.api.put(`/specialist/cases/${caseId}/review`, reviewData);
    return response.data;
  }

  // Legacy method for backward compatibility - use getCasesForReview instead
  getAssignedCases() {
    return this.getCasesForReview();
  }

  // Get a specific case for review
  getCase(caseId: string) {
    if (IS_DEVELOPMENT && ENABLE_MOCK_DATA) {
      const case_ = mockCases.find(c => c.id === caseId);
      return Promise.resolve({ data: case_ });
    }
    return this.api.get(`/cases/${caseId}`); // Use the cases endpoint
  }

  // Update profile
  updateProfile(data: any) {
    if (IS_DEVELOPMENT && ENABLE_MOCK_DATA) {
      return Promise.resolve({ data: { ...data, updated: true } });
    }
    return this.api.put("/specialist/profile", data);
  }
}